import { useState } from 'react';
import { Link } from 'react-router';
import { CheckCircle2, Layers, Save, TriangleAlert } from '@/components/icons';
import { DRAFT_KIND_LABEL, type RequirementSetView, type TestDraft } from '@1qa/shared';
import { draftBlocked } from '@/lib/requirements';
import { Button, Card, CardHeader, Input } from '@/components/ui';

export interface SaveResult {
  saved: { draftId: string; testId: string; name: string }[];
  problems: { draftId: string; name: string; message: string }[];
  group: { id: string; name: string } | null;
}

/** Step 4: the accepted tests, an optional group, Save; then what was saved and what was left out. */
export function SaveStep({
  set,
  drafts,
  saving,
  result,
  onSave,
  onBack,
}: {
  set: RequirementSetView;
  drafts: TestDraft[];
  saving: boolean;
  result: SaveResult | null;
  onSave: (groupName: string | null) => void;
  onBack: () => void;
}) {
  const [withGroup, setWithGroup] = useState(true);
  const [groupName, setGroupName] = useState(`${set.name} tests`.slice(0, 200));
  const toSave = drafts.filter((d) => d.status === 'accepted' && !d.savedTestId);
  const blocked = toSave.filter((d) => draftBlocked(d, set.dataKeys).length);

  return (
    <div className="space-y-6">
      {result && (
        <Card className="p-5" data-testid="requirements-saved">
          {result.saved.length > 0 && (
            <>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
                <CheckCircle2 className="size-4 text-success" aria-hidden /> {result.saved.length} {result.saved.length === 1 ? 'test' : 'tests'} saved
              </h3>
              <ul className="mt-2 space-y-1 text-sm">
                {result.saved.map((s) => (
                  <li key={s.testId}>
                    <Link className="text-link hover:underline" to={`/projects/${set.projectId}/tests/${s.testId}`}>
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-3 flex flex-wrap gap-4 text-sm">
                <Link className="font-medium text-link hover:underline" to={`/projects/${set.projectId}`}>
                  Go to the tests
                </Link>
                {result.group && (
                  <Link className="font-medium text-link hover:underline" to={`/projects/${set.projectId}?tab=groups`}>
                    Open the group “{result.group.name}”
                  </Link>
                )}
              </p>
            </>
          )}
          {result.problems.length > 0 && (
            <div className={result.saved.length ? 'mt-4 border-t border-border-subtle pt-4' : ''}>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
                <TriangleAlert className="size-4 text-warning" aria-hidden /> {result.problems.length} not saved
              </h3>
              <ul className="mt-2 space-y-2 text-sm text-fg-secondary">
                {result.problems.map((p) => (
                  <li key={p.draftId}>
                    <span className="font-medium text-fg">{p.name}</span>: {p.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      <Card>
        <CardHeader title={`4. Save ${toSave.length} ${toSave.length === 1 ? 'test' : 'tests'}`} description="They become ordinary tests in this project, tagged with their requirements so you can find and run them together." />
        {toSave.length ? (
          <ul className="divide-y divide-border-subtle">
            {toSave.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <span className="min-w-0 flex-1 truncate text-fg">{d.name}</span>
                <span className="text-xs text-fg-muted">{DRAFT_KIND_LABEL[d.kind]}</span>
                <span className="font-mono text-xs text-fg-muted">{d.requirementKeys.join(', ')}</span>
                {draftBlocked(d, set.dataKeys).length > 0 && <span className="text-xs text-warning">needs test data</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-6 text-sm text-fg-muted">Nothing left to save. Accept more test cases in step 3.</p>
        )}
        {toSave.length > 0 && (
          <div className="space-y-4 border-t border-border-subtle px-5 py-4">
            <label className="flex items-center gap-2 text-sm text-fg">
              <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={withGroup} onChange={(e) => setWithGroup(e.target.checked)} />
              <Layers className="size-3.5 text-fg-muted" aria-hidden /> Also put them in a new group, to run them together
            </label>
            {withGroup && <Input aria-label="Group name" value={groupName} maxLength={200} onChange={(e) => setGroupName(e.target.value)} />}
            {blocked.length > 0 && (
              <p className="rounded-field bg-warning-subtle p-3 text-xs text-fg">
                {blocked.length} of them use test data that doesn’t exist yet and will be left out. Add it on the Test data page first, or save the
                others now and the rest later.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-3 border-t border-border-subtle px-5 py-4">
          <Button variant="ghost" onClick={onBack}>
            Back to test cases
          </Button>
          <Button
            icon={<Save className="size-4" />}
            loading={saving}
            disabled={!toSave.length || (withGroup && !groupName.trim())}
            onClick={() => onSave(withGroup ? groupName.trim() : null)}
            data-testid="requirements-save"
          >
            Save {toSave.length} {toSave.length === 1 ? 'test' : 'tests'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
